import { useEffect, useState } from "react";
import api from "@/lib/api";

const STATUS_TONE = {
  open: "text-teal-400 border-teal-400/30 bg-teal-400/5",
  closed: "text-zinc-400 border-white/10 bg-white/[0.02]",
  pending: "text-amber-400 border-amber-400/30 bg-amber-400/5",
  rejected: "text-rose-400 border-rose-400/30 bg-rose-400/5",
  error: "text-rose-400 border-rose-400/30 bg-rose-400/5",
};

export default function Trades() {
  const [trades, setTrades] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const load = () => api.get("/trades?limit=200").then((r) => setTrades(r.data)).catch(() => {});
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, []);

  const filtered = filter === "all" ? trades : trades.filter((t) => t.status === filter);
  const totalPnl = trades.filter((t) => typeof t.pnl === "number").reduce((s, t) => s + t.pnl, 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4" data-testid="trades-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Execution Log</div>
          <h1 className="text-xl sm:text-2xl font-medium tracking-tight">Trades</h1>
        </div>
        <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 flex-wrap">
          <div className="text-xs font-mono">
            <span className="text-zinc-500">Realized P&amp;L: </span>
            <span className={`tabular-nums ${totalPnl >= 0 ? "text-teal-400" : "text-rose-400"}`} data-testid="total-pnl">
              {totalPnl >= 0 ? "+" : ""}{totalPnl.toFixed(2)}
            </span>
          </div>
          <div className="flex border border-white/10 rounded-sm overflow-hidden text-[10px] sm:text-xs font-mono uppercase tracking-widest">
            {["all", "open", "closed", "pending", "error"].map((f) => (
              <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
                      className={`px-2 py-1 border-l border-white/10 first:border-l-0 ${
                        filter === f ? "bg-white/[0.06] text-zinc-100" : "text-zinc-500 hover:text-zinc-200"
                      }`}>
                {f}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block border border-white/10 bg-[#121214] rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
              <th className="text-left py-2 px-4">Opened</th>
              <th className="text-left">Pair</th>
              <th className="text-left">Side</th>
              <th className="text-right">Lot</th>
              <th className="text-right">Entry</th>
              <th className="text-right">Exit</th>
              <th className="text-right">P&amp;L</th>
              <th className="text-left">Broker</th>
              <th className="text-left">Mode</th>
              <th className="text-left pr-4">Status</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="text-center text-zinc-500 py-8">No trades yet</td></tr>
            )}
            {filtered.map((t) => (
              <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`trade-row-${t.id}`}>
                <td className="py-2 px-4 text-xs text-zinc-400">{t.opened_at?.slice(11, 19)}</td>
                <td>{t.pair}</td>
                <td className={t.side === "BUY" ? "text-teal-400" : "text-rose-400"}>{t.side}</td>
                <td className="text-right tabular-nums">{t.lot_size}</td>
                <td className="text-right tabular-nums">{t.entry_price?.toFixed(5) || "—"}</td>
                <td className="text-right tabular-nums">{t.exit_price?.toFixed(5) || "—"}</td>
                <td className={`text-right tabular-nums ${t.pnl == null ? "text-zinc-500" : t.pnl >= 0 ? "text-teal-400" : "text-rose-400"}`}>
                  {t.pnl == null ? "—" : (t.pnl >= 0 ? "+" : "") + t.pnl.toFixed(2)}
                </td>
                <td className="text-xs uppercase text-zinc-400">{t.broker}</td>
                <td className="text-xs uppercase text-zinc-400">{t.mode}</td>
                <td className="pr-4">
                  <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${STATUS_TONE[t.status] || ""}`}>
                    {t.status}
                  </span>
                  {t.error && <div className="text-[10px] text-rose-400 mt-0.5 truncate max-w-[200px]" title={t.error}>{t.error}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden border border-white/10 bg-[#121214] rounded-sm divide-y divide-white/5">
        {filtered.length === 0 && (
          <div className="text-center text-zinc-500 py-8 text-sm">No trades yet</div>
        )}
        {filtered.map((t) => (
          <div key={t.id} className="p-3 font-mono" data-testid={`trade-row-${t.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`text-sm font-medium ${t.side === "BUY" ? "text-teal-400" : "text-rose-400"}`}>
                  {t.side}
                </span>
                <span className="text-sm text-zinc-100 truncate">{t.pair}</span>
                <span className={`text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${STATUS_TONE[t.status] || ""}`}>
                  {t.status}
                </span>
              </div>
              <div className={`text-sm tabular-nums shrink-0 ${t.pnl == null ? "text-zinc-500" : t.pnl >= 0 ? "text-teal-400" : "text-rose-400"}`}>
                {t.pnl == null ? "—" : (t.pnl >= 0 ? "+" : "") + t.pnl.toFixed(2)}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-2 text-[11px]">
              <div>
                <div className="text-[9px] uppercase text-zinc-600 tracking-widest">Lot</div>
                <div className="text-zinc-200 tabular-nums">{t.lot_size}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase text-zinc-600 tracking-widest">Entry</div>
                <div className="text-zinc-200 tabular-nums">{t.entry_price?.toFixed(5) || "—"}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase text-zinc-600 tracking-widest">Exit</div>
                <div className="text-zinc-200 tabular-nums">{t.exit_price?.toFixed(5) || "—"}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase text-zinc-600 tracking-widest">Broker</div>
                <div className="text-zinc-400 uppercase">{t.broker}</div>
              </div>
            </div>
            <div className="text-[10px] text-zinc-600 mt-2 flex justify-between">
              <span>{t.opened_at?.slice(11, 19)} · {t.mode}</span>
              {t.error && <span className="text-rose-400 truncate max-w-[60%]" title={t.error}>{t.error}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
